import AssignCaseAutocomplete from "@/components/Forms/AssignCaseAutocomplete.tsx";
import { durationUnitOptions } from "@/components/Forms/SubForms/AddEditTaskTempalteDialog.tsx";
import TaskTemplateAutocomplete from "@/components/Forms/TaskTemplateAutocomplete.tsx";
import { addTaskToCasesByRoleSchema } from "@/components/Forms/schema/addCaseTasksSchema.ts";
import MrtAssignTasksImpactedUsers from "@/components/Table/MrtAssignTasksImpactedUsers.tsx";
import { caseRoles } from "@/constants/caseRoles.ts";
import { useAddCaseTaskByRole } from "@/mutations/useAddCaseTaskByRole.tsx";
import { getErrorMessage } from "@/utils/api.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Divider, Grid2 as Grid, MenuItem } from "@mui/material";
import { RhfTextField } from "mui-rhf-integration";
import { enqueueSnackbar } from "notistack";
import { type BaseSyntheticEvent, type ReactNode, useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

export type AddTaskByRoleFormValues = z.infer<typeof addTaskToCasesByRoleSchema>;

export type RoleCaseTask = {
    duration: string;
    role: string;
    case: {
        id: string;
        name: string;
    };
    taskTemplate: {
        id: string;
        name: string;
    };
};

type Props = {
    handleClose: () => void;
    foundCaseId?: string;
};

const AssignTaskByRoleForm = ({ handleClose, foundCaseId }: Props): ReactNode => {
    const addCaseTaskByRole = useAddCaseTaskByRole();
    const [tasksToCreate, setTasksToCreate] = useState<RoleCaseTask[]>([]);

    const form = useForm<AddTaskByRoleFormValues>({
        resolver: zodResolver(addTaskToCasesByRoleSchema),
        defaultValues: {
            role: "",
            case: null,
            taskTemplate: null,
        },
    });

    const handleSubmit = (values: AddTaskByRoleFormValues) => {
        if (values.role === null || values.case === null || values.taskTemplate === null) {
            return;
        }

        const newTask: RoleCaseTask = {
            duration: values.duration,
            role: values.role,
            case: {
                id: values.case.id,
                name: values.case.name,
            },
            taskTemplate: {
                id: values.taskTemplate.id,
                name: values.taskTemplate.name,
            },
        };

        setTasksToCreate((prevTasks) => [...prevTasks, newTask]);

        form.reset({
            role: "",
            case: null,
            taskTemplate: null,
        });
    };

    const onSubmit: SubmitHandler<AddTaskByRoleFormValues> = async (
        data: AddTaskByRoleFormValues,
        event?: BaseSyntheticEvent,
    ) => {
        event?.stopPropagation();
        handleSubmit(data);
    };

    const doCreateTasks = () => {
        addCaseTaskByRole.mutate(
            {
                tasksToCreate,
            },
            {
                onSuccess: () => {
                    enqueueSnackbar("Note added.", { variant: "success" });
                    form.reset(form.getValues());
                    setTasksToCreate([]);
                    handleClose();
                },
                onError: (error) => {
                    enqueueSnackbar("Error: Add note failed.", { variant: "error" });
                    console.info("error", getErrorMessage(error));
                },
            },
        );
    };

    return (
        <Box>
            <FormProvider {...form}>
                <form>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 3 }} sx={{ pt: 1, py: 2 }}>
                            <RhfTextField
                                select
                                control={form.control}
                                name="role"
                                label="Role"
                                sx={{
                                    width: "100%",
                                    backgroundColor: "#fff",
                                }}
                            >
                                {caseRoles?.map((role) => (
                                    <MenuItem key={`role-${role.id}`} value={`${role.id}`}>
                                        {role.label}
                                    </MenuItem>
                                ))}
                            </RhfTextField>
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }} sx={{ pt: 1, py: 2 }}>
                            <AssignCaseAutocomplete foundCaseId={foundCaseId} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }} sx={{ pt: 1, py: 2 }}>
                            <TaskTemplateAutocomplete />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }} sx={{ pt: 1, py: 2 }}>
                            <RhfTextField
                                select
                                control={form.control}
                                name={"duration"}
                                label={"Duration"}
                                sx={{
                                    width: "100%",
                                }}
                            >
                                {durationUnitOptions.map((durationOption) => (
                                    <MenuItem
                                        key={`duration-${durationOption.days}`}
                                        value={durationOption.days}
                                    >
                                        {durationOption.name}
                                    </MenuItem>
                                ))}
                            </RhfTextField>
                        </Grid>
                        <Grid size={{ xs: 12 }} sx={{ pt: 1, pb: 2, textAlign: "center" }}>
                            <Button variant={"contained"} onClick={form.handleSubmit(onSubmit)}>
                                Prepare Task
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </FormProvider>
            <Grid size={{ xs: 12 }} sx={{ pt: 1, textAlign: "center" }}>
                <Divider sx={{ mb: 1 }} />
            </Grid>
            <Grid size={{ xs: 12 }} sx={{ pt: 1, textAlign: "center" }}>
                <MrtAssignTasksImpactedUsers
                    tasksToCreate={tasksToCreate}
                    setTasksToCreate={setTasksToCreate}
                />
            </Grid>
            <Grid size={{ xs: 12 }} sx={{ py: 2, textAlign: "center" }}>
                <Button
                    variant={"contained"}
                    onClick={doCreateTasks}
                    disabled={tasksToCreate.length === 0}
                >
                    Create Tasks
                </Button>
            </Grid>
        </Box>
    );
};

export default AssignTaskByRoleForm;
